import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import api from '../api/client';

// Dopo un cambio di tariffa oraria: da che mese vale? Le presenze già inserite da quel mese in poi
// vengono ricalcolate con la tariffa nuova; quelle prima restano com'erano. I mesi già chiusi in
// Paghe Mensili non si toccano (vedi ricalcolaPresenzeTariffa in dipendenti.service.ts).
const MESI = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];
const euro = (n) => `€${(n || 0).toFixed(2)}`;

export default function RicalcoloTariffaModal({ dipendenteId, tariffaPrima, tariffaDopo, onClose, onApplicato }) {
  const oggi = new Date();
  const [anno, setAnno] = useState(oggi.getFullYear());
  const [mese, setMese] = useState(oggi.getMonth() + 1);
  const [anteprima, setAnteprima] = useState(null);
  const [errore, setErrore] = useState(null);
  const [applicando, setApplicando] = useState(false);

  useEffect(() => {
    setAnteprima(null); setErrore(null);
    api.get(`/dipendenti/${dipendenteId}/ricalcolo-tariffa`, { params: { da_anno: anno, da_mese: mese } })
      .then(r => setAnteprima(r.data))
      .catch(e => setErrore(e.response?.data?.error || e.message));
  }, [dipendenteId, anno, mese]);

  const applica = async () => {
    setApplicando(true);
    try {
      const r = await api.post(`/dipendenti/${dipendenteId}/ricalcolo-tariffa`, { da_anno: anno, da_mese: mese });
      onApplicato(r.data);
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.error || e.message));
    } finally { setApplicando(false); }
  };

  const mesiChiusi = anteprima?.mesi.filter(m => m.chiuso) || [];
  const anni = [oggi.getFullYear() - 1, oggi.getFullYear(), oggi.getFullYear() + 1];

  return (
    <div style={s.overlay}>
      <div style={s.modal}>
        <div style={s.header}>
          <div style={s.title}>TARIFFA CAMBIATA: {euro(tariffaPrima)}/h → {euro(tariffaDopo)}/h</div>
          <button style={s.iconBtn} onClick={onClose} title="Chiudi"><X size={16}/></button>
        </div>
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <p style={s.p}>
            Le presenze già inserite hanno ancora l'importo calcolato con la tariffa vecchia.
            Da che mese vale la nuova tariffa? Ricalcolo le presenze da quel mese in poi, quelle dei mesi precedenti restano come sono.
          </p>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={s.label}>VALE DA</span>
            <select style={s.sel} value={mese} onChange={e => setMese(parseInt(e.target.value, 10))}>
              {MESI.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
            <select style={s.sel} value={anno} onChange={e => setAnno(parseInt(e.target.value, 10))}>
              {anni.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>

          {errore && <div style={{ ...s.p, color: '#EF4444' }}>{errore}</div>}
          {!errore && !anteprima && <div style={s.p}>Calcolo…</div>}
          {anteprima && (anteprima.mesi.length === 0 ? (
            <div style={s.p}>Nessuna presenza da {MESI[mese - 1]} {anno} in poi: la nuova tariffa varrà per le presenze che inserirai.</div>
          ) : (
            <div style={{ border: '1px solid var(--border,#1E1E1E)', borderRadius: '8px', overflow: 'hidden' }}>
              <div style={{ ...s.riga, ...s.intest }}><span>MESE</span><span>PRESENZE</span><span>OGGI</span><span>DOPO</span></div>
              {anteprima.mesi.map(m => (
                <div key={`${m.anno}-${m.mese}`} style={{ ...s.riga, opacity: m.chiuso ? 0.5 : 1 }}>
                  <span>{MESI[m.mese - 1]} {m.anno}{m.chiuso ? ' · chiuso' : ''}</span>
                  <span>{m.n}</span>
                  <span>{euro(m.prima)}</span>
                  <span style={{ fontWeight: 700, color: m.dopo !== m.prima ? '#F4600C' : 'inherit' }}>{euro(m.dopo)}</span>
                </div>
              ))}
            </div>
          ))}
          {mesiChiusi.length > 0 && (
            <div style={{ ...s.p, color: '#F59E0B' }}>
              I mesi segnati "chiuso" sono già chiusi in Paghe Mensili: le loro presenze non vengono toccate.
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button style={s.btnSec} onClick={onClose}>NON RICALCOLARE</button>
            <button style={s.btnPri} onClick={applica} disabled={applicando || !anteprima || anteprima.n_da_aggiornare === 0}>
              {applicando ? 'RICALCOLO…' : anteprima?.n_da_aggiornare ? `RICALCOLA ${anteprima.n_da_aggiornare} PRESENZE` : 'NIENTE DA RICALCOLARE'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const s = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' },
  modal:   { background: 'var(--bg)', border: '1px solid var(--border2,#2A2A2A)', borderRadius: '12px', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto' },
  header:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', padding: '12px 16px', borderBottom: '1px solid var(--border,#1E1E1E)' },
  title:   { fontSize: 'var(--font-size-sm)', fontWeight: '700', letterSpacing: '1px' },
  p:       { fontSize: 'var(--font-size-sm)', color: 'var(--text-muted,#888)', margin: 0, lineHeight: 1.5 },
  label:   { fontSize: 'var(--font-size-xs)', letterSpacing: '2px', fontWeight: '700', color: '#555' },
  sel:     { background: 'var(--bg-input,#1A1A1A)', border: '1px solid var(--border2,#2A2A2A)', borderRadius: '8px', padding: '8px 12px', color: 'var(--text,#fff)', fontSize: 'var(--font-size-sm)', outline: 'none' },
  riga:    { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '8px', padding: '8px 12px', fontSize: 'var(--font-size-sm)', borderTop: '1px solid var(--border,#1E1E1E)' },
  intest:  { fontSize: 'var(--font-size-xs)', fontWeight: '700', letterSpacing: '1px', color: '#555', borderTop: 'none' },
  iconBtn: { background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--text-muted,#888)', display: 'inline-flex' },
  btnPri:  { background: '#F4600C', color: '#fff', border: 'none', borderRadius: '8px', padding: '9px 14px', fontSize: 'var(--font-size-xs)', fontWeight: '700', cursor: 'pointer' },
  btnSec:  { background: 'var(--bg-input,#1A1A1A)', color: 'var(--text,#fff)', border: '1px solid var(--border2,#2A2A2A)', borderRadius: '8px', padding: '9px 14px', fontSize: 'var(--font-size-xs)', fontWeight: '700', cursor: 'pointer' },
};
